import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '../../theme/colors';

const FILE_STORAGE_OPTIONS = [
  { label: '250MB', valueMb: 250 },
  { label: '1GB', valueMb: 1024 },
  { label: '2GB', valueMb: 2048 },
  { label: '5GB', valueMb: 5120 },
  { label: '7GB', valueMb: 7168 },
  { label: '25GB', valueMb: 25600 },
  { label: '27GB', valueMb: 27648 },
];

const PRESET_VALUES = new Set(FILE_STORAGE_OPTIONS.map((o) => o.valueMb));

interface FileStorageSectionProps {
  disabled: boolean;
  fileStorageLimitMb: number;
  onFileStorageLimitChange: (valueMb: number) => void;
}

const FileStorageSection: FC<FileStorageSectionProps> = ({
  disabled,
  fileStorageLimitMb,
  onFileStorageLimitChange,
}) => {
  const { t } = useTranslation();
  const isPreset = PRESET_VALUES.has(fileStorageLimitMb);
  const [fileStorageMode, setFileStorageMode] = useState<'custom' | 'defaults'>(
    isPreset ? 'defaults' : 'custom',
  );
  const [fileStorageCustomGb, setFileStorageCustomGb] = useState(
    isPreset ? 0 : fileStorageLimitMb / 1024,
  );

  useEffect(() => {
    if (disabled) {
      if (PRESET_VALUES.has(fileStorageLimitMb)) {
        setFileStorageMode('defaults');
      } else {
        setFileStorageMode('custom');
        setFileStorageCustomGb(fileStorageLimitMb / 1024);
      }
    }
  }, [disabled, fileStorageLimitMb]);

  const handleModeChange = (mode: 'custom' | 'defaults') => {
    setFileStorageMode(mode);
    if (mode === 'defaults') {
      // Pick the closest preset to the current MB value
      const closest = FILE_STORAGE_OPTIONS.reduce(
        (prev, curr) =>
          Math.abs(curr.valueMb - fileStorageLimitMb) <
          Math.abs(prev.valueMb - fileStorageLimitMb)
            ? curr
            : prev,
        FILE_STORAGE_OPTIONS[0],
      );
      onFileStorageLimitChange(closest.valueMb);
    } else {
      // Switching to custom — convert current MB value to GB
      setFileStorageCustomGb(fileStorageLimitMb / 1024);
    }
  };

  const handleCustomGbChange = (gb: number) => {
    setFileStorageCustomGb(gb);
    onFileStorageLimitChange(Math.round(gb * 1024));
  };

  return (
    <>
      <Box
        gap={5}
        maxWidth={540}
        sx={{ alignItems: 'center', display: 'flex', mb: 5 }}
      >
        <Typography
          color="primary"
          sx={{ fontSize: '13px', textTransform: 'uppercase' }}
          variant="text-xs-bold-caps"
        >
          {t('file-storage-label')}
        </Typography>
        <RadioGroup
          onChange={(e) =>
            handleModeChange(e.target.value as 'custom' | 'defaults')
          }
          row
          value={fileStorageMode}
        >
          <FormControlLabel
            control={<Radio size="small" />}
            disabled={disabled}
            label={
              <Typography
                color="primary"
                sx={{ fontSize: '13px' }}
                variant="text-xs-bold-caps"
              >
                {t('defaults')}
              </Typography>
            }
            labelPlacement="start"
            value="defaults"
          />
          <FormControlLabel
            control={<Radio size="small" />}
            disabled={disabled}
            label={
              <Typography
                color="primary"
                sx={{ fontSize: '13px' }}
                variant="text-xs-bold-caps"
              >
                {t('custom')}
              </Typography>
            }
            labelPlacement="start"
            value="custom"
          />
        </RadioGroup>
      </Box>

      <Box
        maxWidth={540}
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          color={colors['gray-700']}
          sx={{ minWidth: 120 }}
          variant="text-xs"
        >
          {t('file-storage')}
        </Typography>
        {fileStorageMode === 'defaults' ? (
          <Select
            disabled={disabled}
            onChange={(e) => onFileStorageLimitChange(Number(e.target.value))}
            size="small"
            sx={{
              '& .MuiSelect-select': {
                color: colors['gray-700'],
                fontSize: '14px',
              },
              flex: 1,
              height: 40,
              maxWidth: 412,
            }}
            value={fileStorageLimitMb}
          >
            {FILE_STORAGE_OPTIONS.map((option) => (
              <MenuItem key={option.valueMb} value={option.valueMb}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <TextField
            InputProps={{
              endAdornment: (
                <Typography
                  sx={{
                    color: colors['gray-450'],
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('gigabytes')}
                </Typography>
              ),
            }}
            disabled={disabled}
            inputProps={{ min: 0 }}
            onChange={(e) => handleCustomGbChange(Number(e.target.value))}
            size="small"
            sx={{
              '& .MuiInputBase-input': {
                color: colors['gray-700'],
                fontSize: '14px',
              },
              '& .MuiInputBase-root': { height: 40, marginTop: 0 },
              flex: 1,
              maxWidth: 412,
            }}
            type="number"
            value={fileStorageCustomGb ?? ''}
          />
        )}
      </Box>
    </>
  );
};

export default FileStorageSection;
